/**
 * Affichage d'une échéance de devoir, commun à « Devoirs & évaluations » et au
 * tableau de bord de l'apprenant (TF-450) : la même ligne se lit pareil aux
 * deux endroits.
 */

import type { TonPastille } from '../components/ui/Primitives';
import type { DevoirApprenant, StatutDevoir } from './useEspaceApprenant';

/** « vendredi 23 mai », en heure de La Réunion. */
export function jourEcheance(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    timeZone: 'Indian/Reunion',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** « Vendredi 23 mai · 23:59 », en heure de La Réunion. */
export function echeanceLongue(iso: string): string {
  const date = new Date(iso);
  const jour = jourEcheance(iso);
  const heure = date.toLocaleTimeString('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${jour.charAt(0).toUpperCase()}${jour.slice(1)} · ${heure}`;
}

/** « 14,5/20 ». */
export function noteSur20(note: number): string {
  return `${String(note).replace('.', ',')}/20`;
}

/** Ce qui reste avant l'échéance, en jours civils. */
export function resteLabel(jours: number): string {
  if (jours < 0) return 'En retard';
  if (jours === 0) return "Aujourd'hui";
  if (jours === 1) return '1 jour';
  return `${jours} jours`;
}

/** Adresse qui ouvre un devoir : le quiz lui-même, sinon sa fiche dépliée. */
export function adresseDevoir(devoir: { id: string; type: string }): string {
  return devoir.type === 'QUIZ' ? `/quiz/${devoir.id}` : `/assignments?devoir=${encodeURIComponent(devoir.id)}`;
}

export const LIBELLES_STATUT_DEVOIR: Record<StatutDevoir, string> = {
  A_RENDRE: 'À rendre',
  RENDU: 'Rendu',
  CORRIGE: 'Corrigé',
};

/**
 * Pastille d'un devoir (TF-453) : le temps qui reste tant qu'il est à rendre,
 * « En retard » une fois l'échéance passée — même le jour même —, sinon son
 * statut, retard compris ; corrigé, sa note publiée (TF-457).
 */
export function pastilleDevoir(
  devoir: Pick<DevoirApprenant, 'statut' | 'enRetard' | 'joursRestants' | 'niveau'> & {
    correction?: { note: number } | null;
  },
): {
  texte: string;
  ton: TonPastille;
} {
  if (devoir.statut === 'CORRIGE') {
    return { texte: devoir.correction ? `Corrigé · ${noteSur20(devoir.correction.note)}` : 'Corrigé', ton: 'success' };
  }
  if (devoir.statut === 'RENDU') return devoir.enRetard ? { texte: 'Rendu en retard', ton: 'warning' } : { texte: 'Rendu', ton: 'info' };
  if (devoir.enRetard) return { texte: 'En retard', ton: 'danger' };
  return { texte: resteLabel(devoir.joursRestants), ton: devoir.niveau };
}
